// What the pull request page reads from GitHub, as functions of the pull
// request and an AbortSignal: the runtime runs each in a lane of the page's
// Program (pr/program.ts) and aborts it when a newer read supersedes it, so
// none of them checks whether its answer is still wanted.
import { contentAt, graphql, GraphQLFailure, isSamlError, rest, restAll } from '../../api/gh';
import { withStacks } from '../../api/nativeStacks';
import type { Priority } from '../../api/scheduler';
import {
  PR_CHECKS,
  PR_DEPLOYMENTS,
  PR_QUERY,
  PR_STACK_QUERY,
  PR_STACK_QUERY_NO_STACKS,
  PR_THREADS_PAGE,
  STACK_POOL,
  PR_TIMELINE_PAGE,
  THREAD_COMMENTS_PAGE,
  replacedCommitsQuery,
  type Annotation,
  type CheckContext,
  type CheckContexts,
  type StackRowPr,
  type PageInfo,
  type PrFile,
  type PullRequestData,
  type ReplacedCommitsResult,
  type RepositoryData,
  type ReviewThread,
  type ThreadComment,
  type TimelineItem,
} from '../../api/queries';
import { generatedMatcher } from '../../logic/categorize';
import type { DeploymentCommit } from '../../logic/previews';
import { readChecks, readCommentsPage, readDeployments, readPr, readStack, readThreadsPage, readTimelinePage } from '../../api/answers';
import { poolAmong, type StackPool } from '../../logic/prStack';
import type { HeldStack } from '../stackHold';
import { parseReplaced, type ReplacedLookup } from '../../logic/range';
import { viewedStates } from '../githubViewed';
import type { RemoteViewed } from '../viewedSync.svelte';

/** Which pull request. */
export interface PrAt {
  owner: string;
  repo: string;
  number: number;
}

/** Timeline pages fetched beyond the first (newest) one; older items beyond are dropped. */
export const MAX_TIMELINE_PAGES = 20;
/** Check-context pages fetched beyond the first. */
export const MAX_CHECK_PAGES = 9;

/** One load of the pull request: everything its page shows from GitHub. */
export interface LoadedPr {
  viewer: string;
  repository: RepositoryData;
  pr: PullRequestData;
  threads: ReviewThread[];
  timeline: TimelineItem[];
  /** Older timeline items were dropped (more than the page cap). */
  truncated: boolean;
  /** GitHub's "Viewed" states of the first page, when they were asked for, and the cursor of the next page (null: none). */
  viewed: (RemoteViewed & { more: string | null }) | null;
}

/** The head's checks, as a checks refresh read them. */
export interface FreshChecks {
  head: string;
  commits: PullRequestData['commits'];
}

/** An annotation with the name of the check run it came from. */
export type CheckAnnotation = Annotation & { check: string };

const vars = (at: PrAt, extra: Record<string, unknown> = {}) => ({ owner: at.owner, repo: at.repo, number: at.number, ...extra });

/** What an error says; GitHub's SAML enforcement in its own words. */
export function message(e: unknown): string {
  if (e instanceof GraphQLFailure) {
    const saml = e.errors.find(isSamlError);
    if (saml) return `GitHub requires SAML single sign-on for this organization: ${saml.message}`;
  }
  return e instanceof Error ? e.message : String(e);
}

/** `done` of `total`, where both are known. */
export interface Fraction {
  done: number;
  total: number;
}

/**
 * How far a load of the pull request is once its first answer is in: what
 * its next request reads, and what it holds so far.
 */
export interface PrProgress {
  reading: 'threads' | 'timeline' | 'checks';
  /** Review threads read so far. */
  threads: number;
  /** Comments read of those the threads hold; null until a thread's further comments are read. */
  comments: Fraction | null;
  /** Timeline items read so far. */
  timeline: number;
  /** The head's checks read, of those it has; null until they are read. */
  checks: Fraction | null;
}

/**
 * The pull request with every thread, comment, timeline page and check;
 * null when GitHub does not show it. With `viewed`, the first page of
 * GitHub's "Viewed" states comes with it, as read from when the load began.
 * `report` is told how far the load is before each request after the first
 * (never when the first answer holds everything).
 */
export async function fetchPr(at: PrAt, viewed: boolean, signal: AbortSignal, report: (p: PrProgress) => void = () => {}): Promise<LoadedPr | null> {
  const viewedAt = Date.now();
  // Every answer below is read as GitHub may send it, with holes (api/answers.ts): what leaves here is whole.
  const res = readPr(await graphql<unknown>(PR_QUERY, vars(at, { viewed }), { signal }));
  const found = res.repository?.pullRequest;
  if (!res.repository || !found) return null;
  let threads = found.reviewThreads.nodes;
  const progress: PrProgress = { reading: 'threads', threads: threads.length, comments: null, timeline: found.timelineItems.nodes.length, checks: null };
  const say = (change: Partial<PrProgress>) => report({ ...Object.assign(progress, change) });
  let tPage = found.reviewThreads.pageInfo;
  while (tPage.hasNextPage) {
    say({ threads: threads.length });
    const more = readThreadsPage(await graphql<unknown>(PR_THREADS_PAGE, vars(at, { after: tPage.endCursor }), { signal }));
    if (!more) break;
    threads = threads.concat(more.nodes);
    tPage = more.pageInfo;
  }
  if (threads.some((t) => t.comments.pageInfo?.hasNextPage)) {
    const comments = { done: threads.reduce((n, t) => n + t.comments.nodes.length, 0), total: threads.reduce((n, t) => n + t.comments.totalCount, 0) };
    say({ threads: threads.length, comments: { ...comments } });
    threads = await Promise.all(
      threads.map((t) =>
        withAllComments(t, signal, (read) => {
          comments.done += read;
          say({ comments: { ...comments } });
        }),
      ),
    );
  }
  // Timeline pages run newest first, so a cap drops the oldest items.
  let timeline = found.timelineItems.nodes;
  let lPage = found.timelineItems.pageInfo;
  for (let i = 0; lPage.hasPreviousPage && i < MAX_TIMELINE_PAGES; i++) {
    say({ reading: 'timeline', threads: threads.length, timeline: timeline.length });
    const more = readTimelinePage(await graphql<unknown>(PR_TIMELINE_PAGE, vars(at, { before: lPage.startCursor }), { signal }));
    if (!more) break;
    timeline = more.nodes.concat(timeline);
    lPage = more.pageInfo;
  }
  // The first page only: the rest are read in the background, once per head.
  const { files, ...rest } = found;
  const viewedFiles: LoadedPr['viewed'] =
    viewed && files
      ? { head: found.headRefOid, at: viewedAt, states: viewedStates(files), more: files.pageInfo.hasNextPage ? files.pageInfo.endCursor : null }
      : null;
  let pr: PullRequestData = rest;
  const head = pr.commits.nodes[0];
  const rollup = head?.commit.statusCheckRollup;
  if (rollup) {
    const contexts = await allContexts(at, rollup.contexts, pr.headRefOid, 'visible', signal, (checks) =>
      say({ reading: 'checks', threads: threads.length, timeline: timeline.length, checks }),
    );
    pr = { ...pr, commits: { ...pr.commits, nodes: [{ ...head, commit: { ...head.commit, statusCheckRollup: { ...rollup, contexts } } }, ...pr.commits.nodes.slice(1)] } };
  }
  return { viewer: res.viewer.login, repository: res.repository, pr, threads, timeline, truncated: lPage.hasPreviousPage, viewed: viewedFiles };
}

/**
 * What is already known of the pull request's stack, without asking
 * GitHub: its stack and when that was read, that it is on its own (as of
 * when), or nothing (null).
 */
export type KeptStack = { kind: 'stack'; pool: StackPool; since: number } | { kind: 'alone'; since: number } | null;

/** The dashboard's rows as its saved copy holds them: every section's and the stack members read by ID, and when the oldest were read. */
export interface SavedRows {
  prs: StackRowPr[];
  hidden: string[];
  at: number;
}

/**
 * What the app already knows of the pull request's stack: the stack
 * another member's page held (`held`), when the pull request is in it;
 * else what the dashboard's saved rows (`saved`) say, a stack or that it is
 * on its own. Null when neither holds the pull request.
 */
export async function keptStack(at: PrAt, held: HeldStack | null, saved: () => Promise<SavedRows | null>): Promise<KeptStack> {
  const repo = `${at.owner}/${at.repo}`;
  if (held && held.repo.toLowerCase() === repo.toLowerCase()) {
    const pool = poolAmong(held.prs, held.hidden, repo, at.number, held.source, held.cut);
    if (pool && pool !== 'alone') return { kind: 'stack', pool, since: held.since };
  }
  const rows = await saved().catch(() => null);
  const found = rows && poolAmong(rows.prs, rows.hidden, repo, at.number, 'dashboard');
  if (!rows || !found) return null;
  return found === 'alone' ? { kind: 'alone', since: rows.at } : { kind: 'stack', pool: found, since: rows.at };
}

/**
 * What GitHub says the pull request's stack is found among, in one
 * request (PR_STACK_QUERY): its native stack's members, and the
 * repository's STACK_POOL most recently updated open pull requests, by
 * whose branches the dashboard's rule finds a stack GitHub does not name.
 * Background work for the scheduler: it never delays what the page waits
 * on. Null when GitHub does not show the pull request.
 */
export async function fetchStack(at: PrAt, signal: AbortSignal): Promise<StackPool | null> {
  // Read as GitHub may send it, with holes (api/answers.ts): every row left is whole.
  const res = readStack(await withStacks<unknown>(PR_STACK_QUERY, PR_STACK_QUERY_NO_STACKS, vars(at, { first: STACK_POOL }), { priority: 'background', signal }));
  const own = res?.repository.pullRequest;
  if (!res || !own) return null;
  const open = res.repository.pullRequests;
  const prs = new Map<string, StackRowPr>();
  for (const p of open.nodes) if (p) prs.set(p.id, { ...p, stack: p.stack ?? null, stackEntry: p.stackEntry ?? null });
  const stack = own.stack && own.stackEntry ? own.stack : null;
  // The members' own stack fields are their entries': the stack, and each one's position in it.
  const named = stack && { id: stack.id, size: stack.size, entries: { nodes: stack.entries.nodes.map((e) => e && { position: e.position, pullRequest: e.pullRequest && { id: e.pullRequest.id, number: e.pullRequest.number } }) } };
  for (const e of stack?.entries.nodes ?? []) if (e?.pullRequest) prs.set(e.pullRequest.id, { ...e.pullRequest, stack: named, stackEntry: { position: e.position } });
  return {
    source: 'github',
    prs: [...prs.values()],
    own: { stack: named, stackEntry: named ? own.stackEntry! : null },
    hidden: [],
    cut: open.totalCount > res.listed,
  };
}

/** A thread with every comment (the first page holds 50); `read` is told how many each further page brought. */
async function withAllComments(t: ReviewThread, signal: AbortSignal, read: (comments: number) => void = () => {}): Promise<ReviewThread> {
  let page: PageInfo | undefined = t.comments.pageInfo;
  if (!page?.hasNextPage) return t;
  let nodes: ThreadComment[] = t.comments.nodes;
  while (page?.hasNextPage) {
    const more = readCommentsPage(await graphql<unknown>(THREAD_COMMENTS_PAGE, { id: t.id, after: page.endCursor }, { signal }));
    if (!more) break;
    nodes = nodes.concat(more.nodes);
    page = more.pageInfo;
    read(more.nodes.length);
  }
  return { ...t, comments: { ...t.comments, nodes, pageInfo: page } };
}

/**
 * Every check context for the head commit (first page plus further pages).
 * `reading` is told, before each further page, how many are read of those
 * the commit has (null when GitHub gave no count).
 */
async function allContexts(
  at: PrAt,
  first: CheckContexts,
  head: string,
  priority: Priority,
  signal: AbortSignal,
  reading: (read: Fraction | null) => void = () => {},
): Promise<CheckContexts> {
  let nodes = first.nodes;
  let page = first.pageInfo;
  for (let i = 0; page?.hasNextPage && i < MAX_CHECK_PAGES; i++) {
    reading(first.totalCount === undefined ? null : { done: nodes.length, total: first.totalCount });
    const pr = readChecks(await graphql<unknown>(PR_CHECKS, vars(at, { after: page.endCursor }), { priority, signal }));
    const ctx = pr?.commits.nodes[0]?.commit.statusCheckRollup?.contexts;
    if (!pr || pr.headRefOid !== head || !ctx) break;
    nodes = nodes.concat(ctx.nodes);
    page = ctx.pageInfo;
  }
  return { ...first, nodes, pageInfo: page };
}

/** Only the head's checks (after a check or status notice); null when GitHub does not show the pull request. */
export async function fetchChecks(at: PrAt, signal: AbortSignal): Promise<FreshChecks | null> {
  const fresh = readChecks(await graphql<unknown>(PR_CHECKS, vars(at, { after: null }), { priority: 'background', signal }));
  if (!fresh) return null;
  let commits = fresh.commits;
  const head = commits.nodes[0];
  const rollup = head?.commit.statusCheckRollup;
  if (rollup) {
    const contexts = await allContexts(at, rollup.contexts, fresh.headRefOid, 'background', signal);
    commits = { ...commits, nodes: [{ ...head, commit: { ...head.commit, statusCheckRollup: { ...rollup, contexts } } }, ...commits.nodes.slice(1)] };
  }
  return { head: fresh.headRefOid, commits };
}

/** The changed files, with the head they are the files of (null: not known). */
export interface LoadedFiles {
  head: string | null;
  files: PrFile[];
}

/** Listings tried before giving up on a pull request whose head keeps moving under them. */
export const FILES_TRIES = 3;

/**
 * The changed files (GitHub's pulls/:n/files), with the head GitHub had
 * for the pull request both before and after the pages were read: the
 * list names no head, and its pages are read one by one, so a push while
 * they are read could mix two heads' files. When the head moved meanwhile
 * the files are listed again, FILES_TRIES times at most; then it fails
 * rather than stamp a head it could not confirm. When GitHub names no
 * head, the head is null. `listed` is told how many files the listing
 * under way holds after each of its pages.
 *
 * The head is read as the pull request's REST resource, revalidated by
 * its ETag (`rest`): GitHub does not count a read that finds it unchanged
 * (a 304), so the check costs a request only when the pull request
 * changed since it was last read, and never a GraphQL point.
 */
export async function fetchFiles(at: PrAt, signal: AbortSignal, listed: (files: number) => void = () => {}): Promise<LoadedFiles> {
  const pull = `repos/${at.owner}/${at.repo}/pulls/${at.number}`;
  const headNow = async () => (await rest<{ head?: { sha?: string } }>(pull, { signal })).head?.sha ?? null;
  let before = await headNow();
  for (let tries = 1; ; tries++) {
    const files = await restAll<PrFile>(`${pull}/files?per_page=100`, { signal, onPage: listed });
    const after = await headNow();
    if (after === before) return { head: after, files };
    if (tries >= FILES_TRIES) throw new Error('This pull request kept changing while its files were listed. Try again in a moment.');
    before = after;
  }
}

/** A completed check run's annotations. */
export async function fetchAnnotations(at: PrAt, run: CheckContext, signal: AbortSignal): Promise<CheckAnnotation[]> {
  const list = await restAll<Annotation>(`repos/${at.owner}/${at.repo}/check-runs/${run.databaseId}/annotations?per_page=100`, {
    priority: 'background',
    signal,
  });
  return list.map((a) => ({ ...a, check: run.name ?? 'check' }));
}

/**
 * Deployments of the pull request's latest commits, read as GitHub may
 * send them (api/answers.ts): a commit whose deployments it withheld (a
 * partial error, for a token that may not read them) has none.
 */
export async function fetchDeployments(at: PrAt, signal: AbortSignal): Promise<DeploymentCommit[]> {
  return readDeployments(await graphql<unknown>(PR_DEPLOYMENTS, vars(at), { signal }));
}

/** The commits the force-pushes before `befores` replaced, against `base`. */
export async function fetchReplaced(at: PrAt, base: string, befores: string[], signal: AbortSignal): Promise<ReplacedLookup> {
  const v: Record<string, unknown> = vars(at, { base });
  befores.forEach((b, i) => (v['b' + i] = b));
  return parseReplaced(await graphql<ReplacedCommitsResult>(replacedCommitsQuery(befores.length), v, { priority: 'background', signal }), befores);
}

/** Which paths the repository's `.gitattributes` at `head` marks generated. */
export async function fetchGenerated(at: PrAt, head: string, signal: AbortSignal): Promise<(path: string) => boolean> {
  return generatedMatcher(await contentAt(at.owner, at.repo, '.gitattributes', head, { signal }));
}
